import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, UserPlus, Check, X, Users as UsersIcon, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Friends = () => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('friends');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [friendsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/friends`, { withCredentials: true }),
        axios.get(`${API}/friend-requests`, { withCredentials: true })
      ]);
      
      setFriends(friendsRes.data);
      setFriendRequests(requestsRes.data);
    } catch (e) {
      console.error('Failed to load friends:', e);
      toast.error('Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  const acceptFriendRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friend-requests/${requestId}/accept`, {}, { withCredentials: true });
      toast.success('Friend request accepted!');
      await loadData();
    } catch (e) {
      toast.error('Failed to accept friend request');
    }
  };

  const declineFriendRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friend-requests/${requestId}/decline`, {}, { withCredentials: true });
      toast.info('Friend request declined');
      await loadData();
    } catch (e) {
      toast.error('Failed to decline friend request');
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/discover')}
            variant="ghost"
            size="icon"
            className="hover:bg-blue-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </Button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
            Friends
          </h1>
          {friendRequests.length > 0 && (
            <Badge className="bg-red-500 text-white">{friendRequests.length}</Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="friends">
              <UsersIcon className="w-4 h-4 mr-2" />
              Friends ({friends.length})
            </TabsTrigger>
            <TabsTrigger value="requests">
              <UserPlus className="w-4 h-4 mr-2" />
              Requests ({friendRequests.length})
            </TabsTrigger>
          </TabsList>

          {/* Friends List */}
          <TabsContent value="friends">
            {friends.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UsersIcon className="w-10 h-10 text-blue-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No Friends Yet</h2>
                <p className="text-gray-500 mb-4">Add friends while discovering profiles!</p>
                <Button
                  onClick={() => navigate('/discover')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
                >
                  Start Discovering
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {friends.map((friend) => (
                  <div
                    key={friend.friendship_id}
                    onClick={() => navigate(`/profile/${friend.user.id}`)}
                    className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg hover:shadow-xl transform hover:-translate-y-1 cursor-pointer"
                    style={{ transition: 'all 0.3s ease' }}
                  >
                    <div className="flex items-center space-x-4">
                      <Avatar className="w-16 h-16">
                        <AvatarImage 
                          src={friend.user.photos?.[0]?.startsWith('http') 
                            ? friend.user.photos[0] 
                            : (friend.user.photos?.[0] ? `${BACKEND_URL}${friend.user.photos[0]}` : friend.user.picture)} 
                        />
                        <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xl">
                          {friend.user.name[0]}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-bold text-gray-800">{friend.user.name}</h3>
                          {friend.user.verification_status === 'approved' && (
                            <div className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full">
                              <CheckCircle className="w-3 h-3 text-blue-600" />
                              <span className="text-xs font-semibold text-blue-700">Verified</span>
                            </div>
                          )}
                        </div>
                        {friend.user.location && (
                          <p className="text-sm text-gray-600">{friend.user.location}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Friends since {new Date(friend.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Friend Requests */}
          <TabsContent value="requests">
            {friendRequests.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-10 h-10 text-purple-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No Friend Requests</h2>
                <p className="text-gray-500">You don't have any pending friend requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {friendRequests.map((request) => (
                  <div
                    key={request.request_id}
                    className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <Avatar 
                        className="w-16 h-16 cursor-pointer"
                        onClick={() => navigate(`/profile/${request.user.id}`)}
                      >
                        <AvatarImage 
                          src={request.user.photos?.[0]?.startsWith('http') 
                            ? request.user.photos[0] 
                            : (request.user.photos?.[0] ? `${BACKEND_URL}${request.user.photos[0]}` : request.user.picture)} 
                        />
                        <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xl">
                          {request.user.name[0]}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 
                            className="text-lg font-bold text-gray-800 cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/profile/${request.user.id}`)}
                          >
                            {request.user.name}
                          </h3>
                          {request.user.verification_status === 'approved' && (
                            <div className="flex items-center space-x-1 bg-blue-100 px-2 py-0.5 rounded-full">
                              <CheckCircle className="w-3 h-3 text-blue-600" />
                              <span className="text-xs font-semibold text-blue-700">Verified</span>
                            </div>
                          )}
                        </div>
                        {request.user.location && (
                          <p className="text-sm text-gray-600">{request.user.location}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Sent {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          onClick={() => acceptFriendRequest(request.request_id)}
                          size="icon"
                          className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 text-white"
                        >
                          <Check className="w-6 h-6" />
                        </Button>
                        <Button
                          onClick={() => declineFriendRequest(request.request_id)}
                          size="icon"
                          variant="outline"
                          className="w-12 h-12 rounded-full border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-6 h-6" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Friends;
